import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ProductDetail } from '../../../../types/ProductTypes';
import { useEditMode } from '../../../../context/EditModeContext';
import { useAuthStore } from '../../../../context/AuthContext';
import { useProductEdit } from '../../../../context/ProductEditContext';
import { useUploadFile } from '../../../../api/useUploadFile';
import { useDownloadFile } from '../../../../api/useDownloadFile';
import { useDeleteFile } from '../../../../api/useDeleteFile';
import { useRenameFile } from '../../../../api/useRenameFile';
import { formatMarpolRegulation } from '../../../../utils/regulationFormatters';
import {
    SectionCard,
    SectionTitle,
    InfoGrid,
    InfoItem,
    ProductNameField,
    FullWidthFields,
    FullWidthItem,
    WarningBox,
    DocumentSection,
    DocumentList,
    DocumentItem,
    DocumentSubSection,
    ScrollableContent,
    UploadButton,
    HiddenFileInput,
    DeleteButton,
    EditButton,
    RenameInput,
    RenameActions,
    ConfirmButton,
    CancelButton,
} from '../../Section.styles';
import { EditableInput, EditableTextarea } from '../../EditableFields.styles';

interface RelatedProduct {
    id: number;
    categoryName: string;
    tradeName: string | null;
    chartererName: string | null;
    synonyms: string;
    isActive?: boolean;
}

interface GeneralInformationSectionProps {
    product: ProductDetail;
    relatedProducts: RelatedProduct[];
    onProductUpdated?: () => void;
}

const GeneralInformationSection: React.FC<GeneralInformationSectionProps> = ({ product, relatedProducts, onProductUpdated }) => {
    const { isInEditMode } = useEditMode();
    const { canEdit } = useAuthStore();
    const productEditContext = useProductEdit();
    const mergePendingChanges = productEditContext?.mergePendingChanges;
    const getPendingChanges = productEditContext?.getPendingChanges;
    const { uploadFile, isUploading, clearMessages } = useUploadFile();
    const { downloadFile } = useDownloadFile();
    const { deleteFile, isDeleting } = useDeleteFile();
    const { renameFile, isRenaming } = useRenameFile();
    const showEditUI = canEdit && isInEditMode;

    // File upload refs
    const msdsFileInputRef = useRef<HTMLInputElement>(null);
    const productDocFileInputRef = useRef<HTMLInputElement>(null);

    // State for renaming files
    const [renamingFile, setRenamingFile] = useState<{ category: string; index: number; originalName: string } | null>(null);
    const [newFileName, setNewFileName] = useState('');

    // Check if this is a product category (no trade name)
    const isProductCategory = !product.tradeName;

    // Helper to extract synonym strings from synonyms (handles both string[] and object[] formats)
    const getSynonymsString = (synonyms: any[] | undefined): string => {
        if (!synonyms || synonyms.length === 0) return '';

        // If synonyms are objects with synonymName property
        if (typeof synonyms[0] === 'object' && synonyms[0] !== null && 'synonymName' in synonyms[0]) {
            return synonyms.map((syn) => syn.synonymName).join(', ');
        }

        // If synonyms are already strings
        return synonyms.join(', ');
    };

    // Form state for editable fields
    const [formData, setFormData] = useState({
        tradeName: product.tradeName || '',
        categoryName: product.category?.name || '',
        chartererName: product.charterer?.name || '',
        casNumber: product.externalIds?.find((id) => id.source === 'CAS')?.value || '',
        synonyms: getSynonymsString(product.synonyms),
        description: '',
        warning: '',
    });

    // Helper to update form and push changes to context
    const updateFormField = useCallback(
        (field: string, value: string) => {
            setFormData((prev) => ({ ...prev, [field]: value }));

            if (!mergePendingChanges) return;

            // Get current pending changes to properly merge comments
            const currentPendingChanges = getPendingChanges ? getPendingChanges() : {};

            // Push change to context based on field type
            switch (field) {
                case 'tradeName':
                    mergePendingChanges({ tradeName: value });
                    break;
                case 'categoryName':
                    mergePendingChanges({ category: { ...product.category, name: value } as any });
                    break;
                case 'chartererName':
                    mergePendingChanges({ charterer: { ...product.charterer, name: value } as any });
                    break;
                case 'casNumber': {
                    const existingCasId = product.externalIds?.find((id) => id.source === 'CAS');
                    const updatedExternalIds = product.externalIds?.map((id) => (id.source === 'CAS' ? { ...id, value } : id)) || [];
                    if (!existingCasId && value) {
                        updatedExternalIds.push({ id: 0, source: 'CAS', value, lastUpdatedUtc: new Date().toISOString() });
                    }
                    mergePendingChanges({ externalIds: updatedExternalIds });
                    break;
                }
                case 'synonyms': {
                    const synonymsArray = value
                        .split(',')
                        .map((s) => s.trim())
                        .filter((s) => s.length > 0)
                        .map((s) => ({ id: 0, synonymName: s }));
                    mergePendingChanges({ synonyms: synonymsArray as any });
                    break;
                }
                case 'description': {
                    // Get base comments from pending changes or product
                    const baseComments = currentPendingChanges.comments || product.comments || [];
                    const descComment = product.comments?.find((c) => c.category === 4);
                    // Keep all non-description comments, replace/add description
                    const otherComments = baseComments.filter((c: any) => c.category !== 4);
                    const updatedComments = [
                        ...otherComments,
                        {
                            id: descComment?.id || 0,
                            text: value,
                            category: 4,
                            visibility: 1,
                            lastUpdatedUtc: new Date().toISOString(),
                        },
                    ];
                    mergePendingChanges({ comments: updatedComments });
                    break;
                }
                case 'warning': {
                    // Get base comments from pending changes or product
                    const baseComments = currentPendingChanges.comments || product.comments || [];
                    const warnComment = product.comments?.find((c) => c.category === 5);
                    // Keep all non-warning comments, replace/add warning
                    const otherComments = baseComments.filter((c: any) => c.category !== 5);
                    const updatedComments = value
                        ? [
                              ...otherComments,
                              {
                                  id: warnComment?.id || 0,
                                  text: value,
                                  category: 5,
                                  visibility: 1,
                                  lastUpdatedUtc: new Date().toISOString(),
                              },
                          ]
                        : otherComments;
                    mergePendingChanges({ comments: updatedComments });
                    break;
                }
            }
        },
        [mergePendingChanges, getPendingChanges, product]
    );

    // Initialize form data when product changes
    useEffect(() => {
        const warningComment = product.comments?.find((comment) => comment.category === 5);
        const descriptionComment = product.comments?.find((comment) => comment.category === 4);
        const description = descriptionComment?.text || '';

        setFormData({
            tradeName: product.tradeName || '',
            categoryName: product.category?.name || '',
            chartererName: product.charterer?.name || '',
            casNumber: product.externalIds?.find((id) => id.source === 'CAS')?.value || '',
            synonyms: getSynonymsString(product.synonyms),
            description: description,
            warning: warningComment?.text || '',
        });
    }, [product]);

    // Handle MSDS file upload
    const handleMsdsFileUpload = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) return;

            clearMessages();

            try {
                await uploadFile({
                    category: 'MSDS',
                    fileName: file.name,
                    file: file,
                });

                // Refresh product data to show new MSDS
                if (onProductUpdated) {
                    await onProductUpdated();
                }
            } catch (err) {
                console.error('Failed to upload MSDS file:', err);
            }

            // Reset file input
            if (msdsFileInputRef.current) {
                msdsFileInputRef.current.value = '';
            }
        },
        [uploadFile, clearMessages, onProductUpdated]
    );

    // Handle Product Document file upload
    const handleProductDocFileUpload = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) return;

            clearMessages();

            try {
                await uploadFile({
                    category: 'ProductDocuments',
                    fileName: file.name,
                    file: file,
                });

                // Add a small delay to ensure server has processed the file
                await new Promise((resolve) => setTimeout(resolve, 500));

                // Refresh product data to show new document
                if (onProductUpdated) {
                    await onProductUpdated();
                }
            } catch (err) {
                console.error('Failed to upload product document:', err);
            }

            // Reset file input
            if (productDocFileInputRef.current) {
                productDocFileInputRef.current.value = '';
            }
        },
        [uploadFile, clearMessages, onProductUpdated]
    );

    // Handle file deletion
    const handleDeleteFile = useCallback(
        async (category: string, fileName: string) => {
            if (!confirm(`Are you sure you want to delete "${fileName}"?`)) {
                return;
            }

            try {
                await deleteFile({ category, fileName });

                // Refresh product data
                if (onProductUpdated) {
                    await onProductUpdated();
                }
            } catch (err) {
                console.error('Failed to delete file:', err);
                alert('Failed to delete file. Please try again.');
            }
        },
        [deleteFile, onProductUpdated]
    );

    // Handle starting file rename
    const handleStartRename = useCallback((category: string, index: number, originalName: string) => {
        setRenamingFile({ category, index, originalName });
        setNewFileName(originalName);
    }, []);

    // Handle canceling rename
    const handleCancelRename = useCallback(() => {
        setRenamingFile(null);
        setNewFileName('');
    }, []);

    // Handle confirming file rename
    const handleConfirmRename = useCallback(async () => {
        if (!renamingFile || !newFileName.trim()) return;

        if (newFileName === renamingFile.originalName) {
            handleCancelRename();
            return;
        }

        try {
            await renameFile({
                category: renamingFile.category,
                oldFileName: renamingFile.originalName,
                newFileName: newFileName.trim(),
            });

            handleCancelRename();

            // Refresh product data
            if (onProductUpdated) {
                await onProductUpdated();
            }
        } catch (err) {
            console.error('Failed to rename file:', err);
            alert('Failed to rename file. Please try again.');
        }
    }, [renamingFile, newFileName, renameFile, onProductUpdated, handleCancelRename]);

    // Warning is in comments[5]
    const warningComment = product.comments?.find((comment) => comment.category === 5);
    const hasWarning = (warningComment && warningComment.text && warningComment.text.trim() !== '') || formData.warning.trim() !== '';

    // Description is in comments[4]
    const descriptionComment = product.comments?.find((comment) => comment.category === 4);
    const description = descriptionComment?.text || 'No description available';

    // CAS Number from externalIds
    const casNumber = product.externalIds?.find((id) => id.source === 'CAS')?.value || 'N/A';

    return (
        <SectionCard>
            <SectionTitle>General Information</SectionTitle>
            <InfoGrid>
                <ProductNameField>
                    <label>Product Name (IBC):</label>
                    {showEditUI && isProductCategory ? (
                        <EditableInput type="text" value={formData.categoryName} onChange={(e) => updateFormField('categoryName', e.target.value)} />
                    ) : (
                        <div>
                            {relatedProducts.length === 0 ? (
                                // Show category name when related products haven't loaded
                                <span>{product.category?.name || 'N/A'}</span>
                            ) : (
                                (() => {
                                    // Show all products without charterer (base category products and variants)
                                    const productsWithoutCharterer = relatedProducts.filter(
                                        (rel) => !rel.chartererName || rel.chartererName.trim() === ''
                                    );

                                    if (productsWithoutCharterer.length === 0) {
                                        // No products without charterer, show category name
                                        return <span>{product.category?.name || 'N/A'}</span>;
                                    }

                                    // Show all products without charterer
                                    return (
                                        <>
                                            {productsWithoutCharterer.map((rel) => {
                                                const isCurrentProduct = rel.id === product.id;
                                                const displayName = rel.tradeName || rel.categoryName;

                                                if (isCurrentProduct) {
                                                    // Current product - show as plain text (no link)
                                                    return <div key={rel.id}>{displayName}</div>;
                                                } else {
                                                    // Other products - show as links
                                                    return (
                                                        <div key={rel.id}>
                                                            <Link
                                                                to={`/product?productId=${rel.id}`}
                                                                title={`Synonyms: ${rel.synonyms || ''}`}
                                                                style={{ color: '#0066cc', textDecoration: 'underline' }}
                                                            >
                                                                {displayName}
                                                            </Link>
                                                        </div>
                                                    );
                                                }
                                            })}
                                        </>
                                    );
                                })()
                            )}
                        </div>
                    )}
                </ProductNameField>
                {!isProductCategory && (
                    <InfoItem>
                        <label>Trade Name:</label>
                        {showEditUI ? (
                            <EditableInput type="text" value={formData.tradeName} onChange={(e) => updateFormField('tradeName', e.target.value)} />
                        ) : (
                            <span>{product.tradeName || 'N/A'}</span>
                        )}
                    </InfoItem>
                )}
                <InfoItem>
                    <label>Regulation:</label>
                    <span>{formatMarpolRegulation(product.category?.regulations?.marpolRegulation) || 'N/A'}</span>
                </InfoItem>
                {!isProductCategory && (
                    <InfoItem>
                        <label>Charterer:</label>
                        {showEditUI ? (
                            <EditableInput
                                type="text"
                                value={formData.chartererName}
                                onChange={(e) => updateFormField('chartererName', e.target.value)}
                            />
                        ) : (
                            <span>{product.charterer?.name || 'N/A'}</span>
                        )}
                    </InfoItem>
                )}
                {!isProductCategory && (
                    <InfoItem>
                        <label>CAS Number:</label>
                        {showEditUI ? (
                            <EditableInput type="text" value={formData.casNumber} onChange={(e) => updateFormField('casNumber', e.target.value)} />
                        ) : (
                            <span>{casNumber}</span>
                        )}
                    </InfoItem>
                )}

                {product.regulations?.tripartiteAgreement?.contains && (
                    <InfoItem>
                        <label>Contains:</label>
                        <span>{product.regulations.tripartiteAgreement.contains}</span>
                    </InfoItem>
                )}
            </InfoGrid>

            {/* Warning Box */}
            {hasWarning && (
                <WarningBox>
                    <label>Warning:</label>
                    {showEditUI ? (
                        <EditableTextarea
                            value={formData.warning}
                            onChange={(e) => updateFormField('warning', e.target.value)}
                            placeholder="Enter warning text..."
                        />
                    ) : (
                        <pre>{warningComment?.text}</pre>
                    )}
                </WarningBox>
            )}

            {/* Full-width fields */}
            <FullWidthFields>
                <FullWidthItem>
                    <label>Synonyms:</label>
                    {showEditUI ? (
                        <EditableInput
                            type="text"
                            value={formData.synonyms}
                            onChange={(e) => updateFormField('synonyms', e.target.value)}
                            placeholder="Enter synonyms separated by commas..."
                        />
                    ) : (
                        <div className="full-width-content">{getSynonymsString(product.synonyms) || 'N/A'}</div>
                    )}
                </FullWidthItem>
                <FullWidthItem>
                    <label>Description:</label>
                    {showEditUI ? (
                        <EditableTextarea
                            value={formData.description}
                            onChange={(e) => updateFormField('description', e.target.value)}
                            placeholder="Enter product description..."
                        />
                    ) : (
                        <div className="full-width-content">{description}</div>
                    )}
                </FullWidthItem>
                <FullWidthItem>
                    <label>Related Products/Trade Names:</label>
                    <ScrollableContent>
                        {relatedProducts.length > 0 ? (
                            (() => {
                                // Exclude products already shown in "Product Name (IBC)" (those without charterer)
                                const productsToShow = relatedProducts.filter((rel) => {
                                    const isCurrentProduct = rel.id === product.id;
                                    const hasCharterer = rel.chartererName && rel.chartererName.trim() !== '';
                                    // Show only products that have a charterer and are not the current product
                                    return !isCurrentProduct && hasCharterer;
                                });

                                if (productsToShow.length === 0) {
                                    return <span>No related products with charterers</span>;
                                }

                                return (
                                    <>
                                        {productsToShow.map((rel) => {
                                            const displayName = rel.tradeName || rel.categoryName;
                                            return (
                                                <div key={rel.id}>
                                                    <Link
                                                        to={`/product?productId=${rel.id}`}
                                                        title={`Synonyms: ${rel.synonyms}`}
                                                        style={{ color: '#0066cc', textDecoration: 'underline' }}
                                                    >
                                                        {displayName}
                                                        {rel.chartererName && ` - ${rel.chartererName}`}
                                                    </Link>
                                                </div>
                                            );
                                        })}
                                    </>
                                );
                            })()
                        ) : (
                            <span>Loading related products...</span>
                        )}
                    </ScrollableContent>
                </FullWidthItem>
            </FullWidthFields>

            {/* Documents Section */}
            <FullWidthFields>
                <FullWidthItem>
                    <DocumentSection>
                        {/* MSDS Documents */}
                        <DocumentSubSection>
                            <h3>Material Safety Data Sheets</h3>
                            {product.materialSafetyDataSheets && product.materialSafetyDataSheets.length > 0 ? (
                                <DocumentList>
                                    {product.materialSafetyDataSheets.map((msds, index) => (
                                        <DocumentItem key={msds.id}>
                                            {showEditUI && (
                                                <DeleteButton
                                                    onClick={() => handleDeleteFile('MSDSDocuments', msds.filePath)}
                                                    disabled={isDeleting}
                                                    title="Delete file"
                                                >
                                                    🗑️
                                                </DeleteButton>
                                            )}
                                            {showEditUI && renamingFile?.category !== 'MSDSDocuments' && (
                                                <EditButton
                                                    onClick={() => handleStartRename('MSDSDocuments', index, msds.filePath)}
                                                    disabled={isRenaming}
                                                    title="Rename file"
                                                >
                                                    ✏️
                                                </EditButton>
                                            )}
                                            <label>MSDS Document {index + 1}:</label>
                                            {renamingFile?.category === 'MSDSDocuments' && renamingFile?.index === index ? (
                                                <>
                                                    <RenameInput
                                                        type="text"
                                                        value={newFileName}
                                                        onChange={(e) => setNewFileName(e.target.value)}
                                                        autoFocus
                                                    />
                                                    <RenameActions>
                                                        <ConfirmButton onClick={handleConfirmRename} disabled={isRenaming} title="Confirm rename">
                                                            ✓
                                                        </ConfirmButton>
                                                        <CancelButton onClick={handleCancelRename} disabled={isRenaming} title="Cancel rename">
                                                            ✗
                                                        </CancelButton>
                                                    </RenameActions>
                                                </>
                                            ) : (
                                                <a
                                                    href="#"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        downloadFile({ category: 'MSDSDocuments', fileName: msds.filePath });
                                                    }}
                                                >
                                                    {msds.filePath}
                                                </a>
                                            )}
                                        </DocumentItem>
                                    ))}
                                </DocumentList>
                            ) : (
                                <span>No MSDS documents available</span>
                            )}

                            {/* MSDS Upload Button */}
                            {showEditUI && (
                                <div>
                                    <UploadButton onClick={() => msdsFileInputRef.current?.click()} disabled={isUploading}>
                                        {isUploading ? 'Uploading...' : 'Upload New MSDS'}
                                    </UploadButton>
                                    <HiddenFileInput ref={msdsFileInputRef} type="file" onChange={handleMsdsFileUpload} />
                                </div>
                            )}
                        </DocumentSubSection>

                        {/* Product Documents */}
                        <DocumentSubSection>
                            <h3>Product Documents</h3>
                            {product.productDocuments && product.productDocuments.length > 0 ? (
                                <DocumentList>
                                    {product.productDocuments.map((doc: any, index: number) => (
                                        <DocumentItem key={doc.id || index}>
                                            {showEditUI && (
                                                <DeleteButton
                                                    onClick={() => handleDeleteFile('ProductDocuments', doc.filePath)}
                                                    disabled={isDeleting}
                                                    title="Delete file"
                                                >
                                                    🗑️
                                                </DeleteButton>
                                            )}
                                            {showEditUI && renamingFile?.category !== 'ProductDocuments' && (
                                                <EditButton
                                                    onClick={() => handleStartRename('ProductDocuments', index, doc.filePath)}
                                                    disabled={isRenaming}
                                                    title="Rename file"
                                                >
                                                    ✏️
                                                </EditButton>
                                            )}
                                            <label>Product Document {index + 1}:</label>
                                            {renamingFile?.category === 'ProductDocuments' && renamingFile?.index === index ? (
                                                <>
                                                    <RenameInput
                                                        type="text"
                                                        value={newFileName}
                                                        onChange={(e) => setNewFileName(e.target.value)}
                                                        autoFocus
                                                    />
                                                    <RenameActions>
                                                        <ConfirmButton onClick={handleConfirmRename} disabled={isRenaming} title="Confirm rename">
                                                            ✓
                                                        </ConfirmButton>
                                                        <CancelButton onClick={handleCancelRename} disabled={isRenaming} title="Cancel rename">
                                                            ✗
                                                        </CancelButton>
                                                    </RenameActions>
                                                </>
                                            ) : (
                                                <a
                                                    href="#"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        downloadFile({ category: 'ProductDocuments', fileName: doc.filePath });
                                                    }}
                                                >
                                                    {doc.filePath}
                                                </a>
                                            )}
                                        </DocumentItem>
                                    ))}
                                </DocumentList>
                            ) : (
                                <span>No product documents available</span>
                            )}

                            {/* Product Document Upload Button */}
                            {showEditUI && (
                                <div>
                                    <UploadButton onClick={() => productDocFileInputRef.current?.click()} disabled={isUploading}>
                                        {isUploading ? 'Uploading...' : 'Upload New Product Document'}
                                    </UploadButton>
                                    <HiddenFileInput ref={productDocFileInputRef} type="file" onChange={handleProductDocFileUpload} />
                                </div>
                            )}
                        </DocumentSubSection>

                        {/* Legacy MSDS (single document) */}
                        {product.materialSafetyDataSheet && (!product.materialSafetyDataSheets || product.materialSafetyDataSheets.length === 0) && (
                            <DocumentSubSection>
                                <h3>Material Safety Data Sheet</h3>
                                <DocumentList>
                                    <DocumentItem>
                                        <label>MSDS Document:</label>
                                        <a
                                            href="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                downloadFile({ category: 'MSDS', fileName: product.materialSafetyDataSheet.filePath });
                                            }}
                                        >
                                            {product.materialSafetyDataSheet.filePath}
                                        </a>
                                    </DocumentItem>
                                </DocumentList>
                            </DocumentSubSection>
                        )}
                    </DocumentSection>
                </FullWidthItem>
            </FullWidthFields>
        </SectionCard>
    );
};

export default GeneralInformationSection;
